/* ==========================================================================
   Claude Bernard — 3D and motion
   No dependencies. Raw WebGL for the bezel, CSS 3D for everything else.
   Safe to load with `defer`. Every effect degrades to static.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.cb-rv:not(.cb-in)');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('cb-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('cb-in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    els.forEach(function (e) { io.observe(e); });
  }

  /* ------------------------------------------------- WebGL machined bezel
     A real bezel ring rather than a torus: flat brushed top face, polished
     chamfers, a coin edge cut as actual geometry, and a marker at twelve.
     ---------------------------------------------------------------------- */
  function initBezel(canvas) {
    if (!canvas || REDUCED) return;
    if (canvas.dataset.cbGl) return;      /* theme editor re-renders: init once */
    canvas.dataset.cbGl = '1';
    var gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: false });
    if (!gl) return;                       // no WebGL: hero falls back to photography

    function compile(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    }

    var vs = compile(gl.VERTEX_SHADER,
      'attribute vec3 p;attribute vec3 n;uniform mat4 mv,pr;' +
      'varying vec3 vn,vp,vl,vo;' +
      'void main(){vl=p;vo=n;vec4 w=mv*vec4(p,1.0);vp=w.xyz;vn=mat3(mv)*n;gl_Position=pr*w;}');

    var fs = compile(gl.FRAGMENT_SHADER,
      'precision highp float;varying vec3 vn,vp,vl,vo;uniform vec3 base,warm;' +
      'void main(){' +
      'vec3 N=normalize(vn);vec3 V=normalize(-vp);' +
      'vec3 L1=normalize(vec3(0.42,0.86,0.72));' +
      'vec3 L2=normalize(vec3(-0.80,-0.22,0.52));' +
      'float d1=max(dot(N,L1),0.0),d2=max(dot(N,L2),0.0);' +
      'vec3 H1=normalize(L1+V),H2=normalize(L2+V);' +
      'float s1=pow(max(dot(N,H1),0.0),120.0);' +
      'float s2=pow(max(dot(N,H2),0.0),40.0);' +
      'float fres=pow(1.0-max(dot(N,V),0.0),3.0);' +
      /* object-space angle drives the radial brushing on the flat faces */
      'float ang=atan(vl.y,vl.x);' +
      'float top=smoothstep(0.55,0.94,abs(vo.z));' +
      'float brush=0.5+0.5*sin(ang*300.0);' +
      'float brush2=0.5+0.5*sin(ang*77.0+1.7);' +
      'float grain=mix(1.0,0.42+1.15*brush*0.7+0.45*brush2,top);' +
      'vec3 col=base*(0.10+0.92*d1+0.30*d2);' +
      'col+=warm*(s1*2.1*grain+s2*0.5)+warm*fres*0.5;' +
      'col*=mix(1.0,0.86+0.30*brush2,top);' +
      /* marker at twelve, on the top face only */
      'float mk=smoothstep(0.085,0.045,abs(ang-1.5708))*top;' +
      'col=mix(col,warm*1.25+vec3(0.16),mk*0.85);' +
      'float a=clamp(0.46+0.46*d1+0.26*fres+s1*1.3+mk*0.35,0.0,0.98);' +
      'gl_FragColor=vec4(col,a);}');

    if (!vs || !fs) return;

    var prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    /* ---- bezel ring: lathed cross-section swept round a knurled radius ---- */
    var SEG = 480, TEETH = 60;
    var Ro = 1.0, Ri = 0.655, HH = 0.115, CH = 0.038, TOOTH = 0.040;
    var TAU = 6.283185307;

    function outerR(u) {
      var k = Math.cos(u * TEETH);
      k = (k < 0 ? -1 : 1) * Math.pow(Math.abs(k), 0.5);   /* squarer teeth */
      return Ro - TOOTH * 0.5 + TOOTH * 0.5 * k;
    }
    /* cross-section, outward then down then back: [radius, z] */
    function section(u) {
      var R = outerR(u);
      return [
        [Ri, HH],          /* inner top edge   */
        [R - CH, HH],      /* top face, outer  */
        [R, HH - CH],      /* top chamfer      */
        [R, -HH + CH],     /* coin edge        */
        [R - CH, -HH],     /* bottom chamfer   */
        [Ri, -HH],         /* bottom face      */
        [Ri, HH]           /* inner wall, closes */
      ];
    }

    var pos = [], nrm = [];
    function tri(a, b, c) {
      var ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
      var vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
      var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      var L = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
      nx /= L; ny /= L; nz /= L;
      pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
      nrm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
    }

    var i, k;
    for (i = 0; i < SEG; i++) {
      var u0 = i / SEG * TAU, u1 = (i + 1) / SEG * TAU;
      var c0 = Math.cos(u0), s0 = Math.sin(u0), c1 = Math.cos(u1), s1s = Math.sin(u1);
      var A = section(u0), B = section(u1);
      for (k = 0; k < 6; k++) {
        var a = [A[k][0] * c0, A[k][0] * s0, A[k][1]];
        var b = [A[k + 1][0] * c0, A[k + 1][0] * s0, A[k + 1][1]];
        var c = [B[k + 1][0] * c1, B[k + 1][0] * s1s, B[k + 1][1]];
        var d = [B[k][0] * c1, B[k][0] * s1s, B[k][1]];
        tri(a, b, c);
        tri(a, c, d);
      }
    }

    function buffer(data) {
      var bo = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, bo);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
      return bo;
    }
    var pb = buffer(pos), nb = buffer(nrm), COUNT = pos.length / 3;

    var aP = gl.getAttribLocation(prog, 'p'), aN = gl.getAttribLocation(prog, 'n');
    var uMV = gl.getUniformLocation(prog, 'mv'), uPR = gl.getUniformLocation(prog, 'pr');

    var accent = (canvas.getAttribute('data-accent') || '#C8BDA0').replace('#', '');
    var ar = parseInt(accent.substr(0, 2), 16) / 255;
    var ag = parseInt(accent.substr(2, 2), 16) / 255;
    var ab = parseInt(accent.substr(4, 2), 16) / 255;

    gl.uniform3f(gl.getUniformLocation(prog, 'base'), 0.30, 0.285, 0.252);
    gl.uniform3f(gl.getUniformLocation(prog, 'warm'), ar, ag, ab);
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    var W, H;
    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var rect = canvas.getBoundingClientRect();
      W = canvas.width = Math.max(2, rect.width * dpr);
      H = canvas.height = Math.max(2, rect.height * dpr);
      gl.viewport(0, 0, W, H);
    }
    resize();
    window.addEventListener('resize', resize);

    var mx = 0, my = 0, tx = 0, ty = 0;
    window.addEventListener('pointermove', function (e) {
      tx = e.clientX / window.innerWidth - 0.5;
      ty = e.clientY / window.innerHeight - 0.5;
    }, { passive: true });

    function perspective(fov, asp, near, far) {
      var f = 1 / Math.tan(fov / 2);
      return new Float32Array([
        f / asp, 0, 0, 0,
        0, f, 0, 0,
        0, 0, (far + near) / (near - far), -1,
        0, 0, 2 * far * near / (near - far), 0]);
    }
    function modelView(rx, ry, tz, sc) {
      var cx = Math.cos(rx), sx = Math.sin(rx), cy = Math.cos(ry), sy = Math.sin(ry);
      var narrow = (W / H) < 1.05;
      var ox = narrow ? 0.16 : 1.06;      /* right of the headline on wide screens */
      var oy = narrow ? -0.86 : -0.30;
      return new Float32Array([
        cy * sc, 0, -sy * sc, 0,
        sx * sy * sc, cx * sc, sx * cy * sc, 0,
        cx * sy * sc, -sx * sc, cx * cy * sc, 0,
        ox, oy, tz, 1]);
    }

    var visible = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { visible = e[0].isIntersecting; }, { threshold: 0 })
        .observe(canvas);
    }

    requestAnimationFrame(function loop(ts) {
      if (!canvas.isConnected) return;    /* section removed in editor: stop */
      requestAnimationFrame(loop);
      if (!visible) return;
      var t = ts * 0.000105;
      mx += (tx - mx) * 0.045;
      my += (ty - my) * 0.045;
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.uniformMatrix4fv(uPR, false, perspective(0.80, W / H, 0.1, 50));
      gl.uniformMatrix4fv(uMV, false, modelView(1.06 + my * 0.34, t + mx * 0.6, -3.15, 1.02));
      gl.bindBuffer(gl.ARRAY_BUFFER, pb);
      gl.enableVertexAttribArray(aP); gl.vertexAttribPointer(aP, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, nb);
      gl.enableVertexAttribArray(aN); gl.vertexAttribPointer(aN, 3, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.TRIANGLES, 0, COUNT);
    });

    setTimeout(function () { canvas.classList.add('cb-up'); }, 260);
  }

  /* -------------------------------------------------- exploded anatomy */
  function initAnatomy(el) {
    if (!el || el.dataset.cbAnat) return;
    el.dataset.cbAnat = '1';
    if (!('IntersectionObserver' in window)) { el.classList.add('cb-go'); return; }
    new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        el.classList.toggle('cb-go', en.isIntersecting && en.intersectionRatio > 0.35);
      });
    }, { threshold: [0, 0.35, 0.6] }).observe(el);
  }

  /* --------------------------------------------------------- pointer tilt */
  function initTilt(root) {
    if (REDUCED) return;
    (root || document).querySelectorAll('.cb-tiltwrap').forEach(function (wrap) {
      var el = wrap.querySelector('.cb-tilt');
      if (!el || el.dataset.cbTilt) return;
      el.dataset.cbTilt = '1';
      wrap.addEventListener('pointermove', function (e) {
        var r = wrap.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.transition = 'transform .08s linear';
        el.style.transform =
          'rotateY(' + (px * 17).toFixed(2) + 'deg) rotateX(' + (-py * 17).toFixed(2) + 'deg) translateZ(26px)';
      });
      wrap.addEventListener('pointerleave', function () {
        el.style.transition = 'transform .7s cubic-bezier(.2,.7,.3,1)';
        el.style.transform = '';
      });
    });
  }

  /* ------------------------------------------------------- depth rail */
  function initRail(rail) {
    if (!rail || REDUCED) return;
    if (rail.dataset.cbRail) return;
    rail.dataset.cbRail = '1';
    function apply() {
      var rr = rail.getBoundingClientRect(), mid = rr.left + rr.width / 2;
      rail.querySelectorAll('.cb-card').forEach(function (c) {
        var cr = c.getBoundingClientRect();
        var d = (cr.left + cr.width / 2 - mid) / rr.width;
        c.style.transform = 'perspective(1400px) rotateY(' + (-d * 17).toFixed(2) +
          'deg) translateZ(' + (-Math.abs(d) * 90).toFixed(0) + 'px)';
      });
    }
    rail.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', apply);
    setTimeout(apply, 300);
  }

  /* -------------------------------------------------------------- boot */
  function boot(root) {
    initReveal(root);
    initTilt(root);
    (root || document).querySelectorAll('.cb-bezel').forEach(initBezel);
    (root || document).querySelectorAll('.cb-anat').forEach(initAnatomy);
    (root || document).querySelectorAll('.cb-rail').forEach(initRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Shopify theme editor: re-init when a section is re-rendered */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();